import { getCookie } from 'react-use-cookie'

import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject
} from '@apollo/client'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import * as Sentry from '@sentry/react'
import crossFetch from 'cross-fetch'
import Observable from 'zen-observable'

import { ClientSession } from 'src/core/sessionTokenService'
import { reportErrorMessageWithData } from 'src/lib/js/clientError'
import { getSessionSource } from 'src/shared/components/common/authentication'
import {
  PW_ACCESS,
  PWLESS_ACCESS,
  PWLESS_REFRESH
} from 'src/shared/components/common/authentication/constants'
import type {
  LaunchDarklyFlags,
  LaunchDarklyFlagsKebab
} from 'src/shared/components/common/feature_flags/featureFlags'
import { isGuestProfileCreated, setTokens } from 'src/shared/js/authUtils'

import { resources } from 'config'

import { PasswordlessRefreshTokenDocument } from './onlineOrdering'
import ooIntrospectionQueryData from './ooPossibleTypes'
import { createPersistedQueriesLink } from './persistedQueriesLink'
import introspectionQueryData from './sitesPossibleTypes'
import TimeoutLink from './timeoutLink'

declare global {
  interface Window {
    __TOAST_BANQUET_INITIAL_DATA__?: {
      session?: ClientSession
    }
  }
}

export type ApolloBatchingOptions = {
  /** Defaults to true */
  enabled: boolean
  options?: BatchHttpLink.Options
}

export type CreateClientOptions = {
  uri: string
  /**
   * If desired, the cache can be hydrated with a prefetched state.
   */
  state?: NormalizedCacheObject
  includeCredentials?: boolean
  batchingOptions?: ApolloBatchingOptions
  additionalHeaders?: Record<string, string>
  withRetry?: boolean
  timeout?: number
  ldFlags?: LaunchDarklyFlags | LaunchDarklyFlagsKebab
}

/**
 * Common options to use when batching is desired. This does not
 * imply that batching is generally enabled by default, only that these
 * defaults may be used when desired.
 */
export const BATCHING_ENABLED_DEFAULTS: ApolloBatchingOptions = Object.freeze({
  enabled: true
})

// Observable needed to get promises to work in onError
// https://github.com/apollographql/apollo-link/issues/646
const promiseToObservable = (promise: Promise<any>) =>
  new Observable((subscriber: any) => {
    promise.then(
      (value) => {
        if (!subscriber.closed) {
          if (
            value?.data?.passwordlessRefreshToken?.code ===
            'UNKNOWN_OR_INVALID_REFRESH'
          ) {
            subscriber.error(value)
          } else {
            subscriber.next(value)
            subscriber.complete()
          }
        }
      },
      (err) => subscriber.error(err)
    )
  })

const hasAuthError = (graphQLErrors: any) =>
  graphQLErrors &&
  graphQLErrors.findIndex(
    (err: any) => err?.extensions?.code === 'UNAUTHENTICATED'
  ) !== -1
const handleAuthError = (operation: any, forward: any) =>
  promiseToObservable(refreshAuthToken()).flatMap((value) => {
    // attach the refreshed auth token so that the old token isn't reused when retrying the query
    if (value) {
      let headers = operation.getContext().headers
      headers['Authorization'] = `Bearer ${value}`
      operation.setContext({ headers })
    }
    return forward(operation)
  })

export const serverErrorLink = onError(
  // @ts-ignore
  ({ response, networkError, graphQLErrors, operation, forward }) => {
    if (graphQLErrors) {
      // eslint-disable-next-line array-callback-return
      graphQLErrors.map(({ message, locations, path }) => {
        if (response) {
          // @ts-ignore
          response.errorMessage = message
        }
        console.error(
          `[Request error]: Message: ${message}, Location: ${locations}, Path: ${path}, Operation: ${operation.operationName}`
        )
      })
    }
    if (networkError) {
      console.error(`[Network error]: ${networkError.message}`, networkError)
    }
    if (hasAuthError(graphQLErrors)) {
      return handleAuthError(operation, forward)
    }
  }
)

const clientErrorLink = onError(
  // @ts-ignore
  ({ networkError, graphQLErrors, operation, forward }) => {
    if (networkError) {
      console.error(`[Network error]: ${networkError.message}`, networkError)
    }

    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, extensions }) => {
        // Log all GRAPHQL_VALIDATION_FAILED errors to sentry regardless of environment.
        // This is most likely caused by a query using the wrong ApolloClient instance.
        // E.g. you're using a `useQuery()` hook that requires the ooClient but you're within an
        // ApolloProvider that uses the sitesApiClient. In this case, you need to use the
        // `useOOClient()` hook and pass it in as `useQuery()`'s `client` option.
        //
        // Log all other errors to sentry but when in a non-production environment.
        if (
          extensions?.code === 'GRAPHQL_VALIDATION_FAILED' ||
          (process.env.NODE_ENV !== 'production' &&
            process.env.NODE_ENV !== 'prod')
        ) {
          const restaurantGuid = operation?.variables?.input?.restaurantGuid
          reportErrorMessageWithData(
            `Graphql Error: an error occurred for operation: ${operation.operationName} on restaurantGuid: ${restaurantGuid} - ${message}`,
            extensions
          )
        }
      })
    }

    if (hasAuthError(graphQLErrors)) {
      return handleAuthError(operation, forward)
    }
  }
)

const legacyAuthLink = setContext((_, { headers }) => {
  const pwlessAccessToken = getCookie(PWLESS_ACCESS)
  const guestProfileCreated = isGuestProfileCreated(pwlessAccessToken)
  const pwAccessToken = getCookie(PW_ACCESS)

  const newHeaders: { [key: string]: string } = {}
  // A value of null for guestProfileCreated indicates a bad token. It will get refreshed by logic
  // in CustomerContext.
  if (pwlessAccessToken && guestProfileCreated) {
    newHeaders['Authorization'] = `Bearer ${pwlessAccessToken}`
  }
  if (pwAccessToken) {
    newHeaders['toast-customer-access'] = pwAccessToken
  }

  return {
    headers: {
      ...headers,
      ...newHeaders
    }
  }
})

const securityTokenHeaderLinkFactory = (sessionToken: string) =>
  setContext((_, { headers: originalHeaders }) => {
    return {
      headers: {
        ...originalHeaders,
        'Toast-Session-ID': sessionToken
      }
    }
  })

export const getSessionToken = () => {
  try {
    const tokenObj =
      typeof document !== 'undefined'
        ? document.getElementById('session')?.getAttribute('data-content')
        : null

    if (!tokenObj) {
      // Try to resolve session for toastweb
      if (
        typeof window !== 'undefined' &&
        window.__TOAST_BANQUET_INITIAL_DATA__?.session
      ) {
        const banquetClientSession =
          window.__TOAST_BANQUET_INITIAL_DATA__.session
        // don't attempt to reload the page on an expired Banquet client session, sites-admin-spa will handle that
        return banquetClientSession.id
      }
      return undefined
    }
    const clientSession = JSON.parse(atob(tokenObj)) as ClientSession
    // Reload the page if the session is expired, or set a timeout to ensure it will reload once it expires
    const now = new Date(Date.now())
    if (new Date(clientSession.expiresAt) < now) {
      window.location.reload()
    } else {
      setTimeout(
        () => window.location.reload(),
        new Date(clientSession.expiresAt).getTime() - now.getTime()
      )
    }
    return clientSession.id
  } catch (e) {
    return undefined
  }
}

const CREATE_CLIENT_DEFAULTS: CreateClientOptions = {
  uri: resources.apiEndpoint
}

/**
 * Creates an Apollo client. Clients created using this function
 * may use an auth token fetched from the browser's cookies
 * or the server's M2M oauth token, depending on the options and
 * the context in which this function is called.
 */
export const createClient = (options: CreateClientOptions) => {
  const {
    uri,
    state,
    includeCredentials,
    batchingOptions,
    additionalHeaders,
    withRetry,
    timeout
  } = { ...CREATE_CLIENT_DEFAULTS, ...options }

  // We've only enabled persisted queries for do-federated-gateway so far.
  // Disable persisted queries in development to avoid PersistedQueryNotFound errors
  // for queries that haven't been uploaded to the server yet
  const isDevelopment = process.env.NODE_ENV === 'development'
  const withPersistedQueries =
    uri === resources.federatedProxyHost && !isDevelopment

  const isSsr = typeof window === 'undefined'
  const cache = new InMemoryCache({
    possibleTypes: {
      ...introspectionQueryData.possibleTypes,
      ...ooIntrospectionQueryData.possibleTypes
    },
    typePolicies: {
      ColorConfig: { merge: true },
      Image: { merge: true },
      MenuConfig: { merge: true },
      MenuFormatConfig: { merge: true },
      OnlineOrderingConfig: { merge: true },
      Restaurant: {
        merge: true,
        fields: {
          config: { merge: true },
          pixelsV2: { merge: true },
          socialMediaLinks: { merge: true }
        }
      },
      RestaurantConfig: { merge: true },
      SiteContent: { merge: true },
      // Product variant Apollo cache configuration to prevent cache collisions:
      // - CatalogProductInfo: Wrapper type with no ID, prevent normalization
      // - CatalogProductVariant: Normalize by unique 'id' for proper caching
      // - CatalogProductVariantOption: Option IDs are shared across variants but
      //   represent different selected values per variant, so disable normalization to
      //   prevent Apollo from incorrectly merging options across different variants
      // - CatalogCustomAttribute: definition ids are shared by every item while 'values' is item-scoped, so disable normalization
      CatalogProductInfo: {
        keyFields: false
      },
      CatalogProductVariant: {
        keyFields: ['id']
      },
      CatalogProductVariantOption: {
        keyFields: false
      },
      CatalogCustomAttribute: {
        keyFields: false
      },
      // rankedToastFundedOffers is a field of OffersQuery reached via Query.offers,
      // so a policy under Query.fields below would never bind.
      OffersQuery: {
        fields: {
          rankedToastFundedOffers: { keyArgs: ['input', ['restaurantGuid']] }
        }
      },
      Query: {
        fields: {
          offers: { merge: true },
          marketing: { merge: true },
          menusV3: {
            keyArgs: [
              'input',
              [
                'restaurantGuid',
                'respectAvailability',
                'hideOutOfStockItems',
                'channelGuid',
                'dateTime'
              ]
            ]
          },
          paginatedMenuItems: {
            keyArgs: [
              'input',
              [
                'restaurantGuid',
                'respectAvailability',
                'hideOutOfStockItems',
                'channelGuid',
                'filters',
                'offset',
                'limit',
                'slug',
                'dateTime',
                'consolidateItemsByProduct',
                'sort',
                'filterCriteria',
                'includeAvailableFilters',
                'includeProductOptionFilters',
                'includeSlugs'
              ]
            ]
          },
          doMenus_getMenuGroupsPaginated: {
            keyArgs: [
              'input',
              ['groups', 'visibility', 'respectAvailability', 'offset']
            ]
          },
          popularItems: { keyArgs: ['input', ['restaurantGuid']] },
          rankedPromoOffers: { keyArgs: ['input', ['restaurantGuid']] },
          restaurantByDomain: { merge: true, keyArgs: ['domain'] }
        }
      }
    }
  })

  // Define a customFetch to override cross-fetch to decorate the outbound request
  // with all batched graphQL operations and persistent query hashes. We need to set
  // the hash header here otherwise BatchHttpLink will not work correctly.
  // See comment in persistentQueriesLink.ts.
  const customFetch = (uri: any, options: RequestInit) => {
    try {
      // Parse the body to get the operations
      const body = JSON.parse(options.body?.toString() || '')
      let operationNames = ''
      let hashes = ''
      if (Array.isArray(body)) {
        operationNames = body
          .map(
            (op: { operationName: any }) =>
              op.operationName || 'UnnamedOperation'
          )
          .sort() // sort these operations alphabetically in ascending order
          .join(',')
        hashes = body
          .map((op: any) => op?.extensions?.persistedQuery?.sha256Hash)
          .filter((hash: any) => Boolean(hash))
          .join(',')
      } else {
        operationNames = body?.operationName || 'UnnamedOperation'
        hashes = body?.extensions?.persistedQuery?.sha256Hash || ''
      }

      // Modify the request headers to include the operation names for every graphQL operation (query/mutation)
      options.headers = {
        ...options.headers,
        'Toast-GraphQL-Operation': operationNames,
        'Toast-Persistent-Query-Hash': hashes
      }
    } catch (error) {
      // if this decoration fails for ANY reason make sure that the request is never blocked, just move along
      Sentry.captureException(
        `ERROR: an error occurred decorating apollo request with headers ${error}`
      )
    }

    // Now call cross-fetch with the modified options
    return crossFetch(uri, options)
  }

  const linkOptions = {
    fetch: customFetch,
    uri,
    credentials: includeCredentials ? 'include' : 'none',
    headers: additionalHeaders || undefined
  }

  const httpLink = batchingOptions?.enabled
    ? new BatchHttpLink({ ...linkOptions, ...(batchingOptions?.options || {}) })
    : new HttpLink(linkOptions)

  const errorLink = isSsr ? serverErrorLink : clientErrorLink

  const links: ApolloLink[] = []

  links.push(new TimeoutLink(timeout))

  if (includeCredentials) {
    links.push(legacyAuthLink)
  }

  const sessionToken = getSessionToken()

  if (sessionToken) {
    links.push(securityTokenHeaderLinkFactory(sessionToken))
  }

  if (withRetry) {
    links.push(
      new RetryLink({
        delay: { initial: 200, max: 1000, jitter: true },
        attempts: {
          max: 4,
          retryIf: (error, _operation) => {
            return error?.message === 'Network request failed'
          }
        }
      })
    )
  }

  links.push(errorLink)

  if (withPersistedQueries) {
    links.push(
      createPersistedQueriesLink({
        useGETForHashedQueries: !batchingOptions?.enabled
      }),
      httpLink
    )
  } else {
    // Terminal link needs to be last.
    links.push(httpLink)
  }

  return new ApolloClient({
    ssrMode: isSsr,
    link: ApolloLink.from(links),
    cache: state ? cache.restore(state) : cache,
    name: process.env.toast_svc_name || 'sites-web-client',
    version:
      process.env.toast_service_revision || process.env.VERSION || 'unknown'
  })
}

// Don't include the access token auth header (i.e. set includeCredentials to false) in this client. If refreshing, the token might be malformed, which can cause an error.
export const createAuthRefreshClient = ({
  ldFlags
}: {
  ldFlags?: LaunchDarklyFlags | LaunchDarklyFlagsKebab
} = {}) =>
  createClient({
    uri: resources.federatedProxyHost,
    timeout: resources.clientQueryTimeoutMs,
    ldFlags
  })

const authRefreshClientFallback = createAuthRefreshClient()

export const refreshAuthToken = async (
  authRefreshClient: ReturnType<
    typeof createAuthRefreshClient
  > = authRefreshClientFallback,
  onSetTokens?: (accessToken: string) => void
) => {
  const currRefresh = getCookie(PWLESS_REFRESH)

  if (!currRefresh) {
    return null
  }

  const result = await authRefreshClient.mutate({
    mutation: PasswordlessRefreshTokenDocument,
    // refreshToken is populated by the server
    variables: {
      input: { refreshToken: currRefresh, source: getSessionSource() }
    }
  })

  if (
    result.data?.passwordlessRefreshToken?.__typename ===
    'PasswordlessTokenResponse'
  ) {
    setTokens(
      result.data.passwordlessRefreshToken.accessToken,
      result.data.passwordlessRefreshToken.expiresAtIso8601,
      result.data.passwordlessRefreshToken.refreshToken,
      onSetTokens
    )
    return result.data.passwordlessRefreshToken.accessToken
  }

  return null
}
